import { MasterAccountInfo } from 'ctp-user-auth-service';
import { DatInstanceTypeEnum, InstanceRelated } from 'ctp-toll-domain';
import { DatInstancesState, InstancesState, DatInstanceFlagEnum, DatInstanceStateEnum, INotification } from './types';

interface InstanceStatus {
  state: DatInstanceStateEnum,
  flag: DatInstanceFlagEnum
}

/**
 * List all the possible states (key) of a DSRC instance and their conditions (value as function: boolean)
 * See https://wiki.int.be.continental.cloud/pages/viewpage.action?pageId=1126105266 for detailed conditions
 */
const instanceStateMap = new Map([
  ['inactive', (instance: InstanceRelated) => instance.activeRules && instance.activeRules.length < 1 && instance.pastRules && instance.pastRules.length < 1],
  ['active', (instance: InstanceRelated) => (instance.activeRules && instance.activeRules.length > 0) || (instance.pastRules && instance.pastRules.length > 0)],
  ['archived', (instance: InstanceRelated) => instance.deleted]
]);

/**
 * Filters a map object and returns the state that is true
 * A DAT can be active with a request to be archived, so 'requested' is last in the loop
 * Note: A method Map.GroupBy exists but is not fully supported by all browsers
 * @param dat Dat
 * @returns state string The key that represents the state of the DAT (from the map) 'Inactive' by default
 */
const getCurrentState = (instance: InstanceRelated) => {
  let state;
  instanceStateMap.forEach((_, k, self) => {
    if (self.get(k)!(instance)) {
      state = k;
    }
  });
  return state || 'inactive';
};

const getters = {
  /**
   * Returns the different states of a DSRC instance depending on conditions defined in
   * https://wiki.int.be.continental.cloud/pages/viewpage.action?pageId=1126105266
   * @param instance the DSRC instance object
   * @returns InstanceStatus
   */
  getInstanceStatus: () => (instance: InstanceRelated): InstanceStatus => {
    const isUsed = instance.activeRules && instance.activeRules.length > 0 ? DatInstanceFlagEnum.Used : DatInstanceFlagEnum.Unused;
    const instanceStatus: InstanceStatus = { state: getCurrentState(instance) as DatInstanceStateEnum, flag: isUsed };
    return instanceStatus;
  },
  getDatInstances(state: DatInstancesState) {
    return state.instances.sort((a, b) => a.name.localeCompare(b.name));
  },
  getNonArchivedDatInstances(state: DatInstancesState) {
    return state.instances.filter(a => !a.deleted);
  },
  getInstanceByEid(state: DatInstancesState) {
    return state.fileByEid;
  },
  getCCCDatInstances: (state: InstancesState) => (master: MasterAccountInfo) => state.instances.filter(i => i.type === DatInstanceTypeEnum.Ccc && i.masterId === master.id),
  getEFCDatInstances: (state: InstancesState) => (master: MasterAccountInfo) => state.instances.filter(i => i.type === DatInstanceTypeEnum.Efc && i.masterId === master.id),
  getUNIDatInstances: (state: InstancesState) => (master: MasterAccountInfo) => state.instances.filter(i => i.type === DatInstanceTypeEnum.Uni && i.masterId === master.id),
  getLACDatInstances: (state: InstancesState) => (master: MasterAccountInfo) => state.instances.filter(i => i.type === DatInstanceTypeEnum.Lac && i.masterId === master.id),
  getErrorMessage(state: DatInstancesState): string {
    return state.errorMessage;
  },
  getNotification(state: DatInstancesState): INotification | undefined {
    return state.notification;
  }
};
export default getters;
